import { useLazyQuery, useMutation } from "@apollo/client";
import { Button, Grid, TextField, ThemeProvider, Typography, useMediaQuery, useTheme } from "@material-ui/core";
import { FilterList } from "@material-ui/icons";
import Dialog from "components/Dialog";
import NavSwitcher, { TabProps } from "components/NavSwitcher";
import { DefaultInfo } from "components/NothingRight";
import SearchBox from "components/SearchBox";
import { Slider } from "components/Slider/Slider";
import Table from "components/Table";
import { ROW_PER_PAGE_OPTIONS } from "config";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { parseTourStops } from "domains/Request/component/shared/utils";
import { Slogan } from "domains/Task/components/Slogan/Slogan";
import {
  Axe,
  AxeInput,
  Course,
  ERequestType,
  Mission,
  MissionStatusType,
  PapsService,
  SubTask,
  SubTaskStatus,
  Task,
  TaskType,
} from "generated/graphql";
import { FETCH_MISSION_BADGE_BY_TYPES, GET_ALL_MISSIONS } from "graphql/Missions/queries";
import { SUBTASK_RESCHEDULING_BY_CLIENT } from "graphql/Task/mutations";
import { useCurrentUser } from "hooks";
import React, { useState } from "react";
import "styled-components/macro";
import { formatDate, makeSearch } from "utils";
import { isSuccess } from "utils/https";
import { COURSE_BADGES, GET_ALL_COURSES } from "graphql/course/queries";
import { useCountryCode } from "hooks/useCurrentUser";
import { ALL_TASKS } from "graphql/Task/queries";
import { subtaskActions, taskColumns } from "./data";
import { styleIcon, useStyles } from "./style";
import TaskDetailsMAD from "./TaskDetailsMAD";
import TaskDetailsMsg from "./TaskDetailsMsg";
import TaskDetailsNT from "./TaskDetailsNT";
import TaskDetailTitle from "./TaskDetailTitle";
import TaskDetailsTR from "./TaskDetailsTR";
import { CourseDetails } from "./CourseDetails";

const useTabs = (madCount = 0, trCount = 0, msgCount = 0, ntCount = 0, crsCount = 0): TabProps[] => {
  const { text } = useText();
  return [
    {
      libelle: `Messagerie`,
      id: "MSG",
      count: msgCount,
    },
    {
      libelle: `D. Courier`,
      id: "MAD",
      count: madCount,
    },

    {
      libelle: `N.Transport`,
      id: "NT",
      count: ntCount,
    },
    {
      libelle: `Paps Tour`,
      id: "TR",
      count: trCount,
    },
    {
      libelle: `Course`,
      id: "Course",
      count: crsCount,
    },
  ];
};

export interface TaskProps {
  defaultTapvalue?: number;
  status?: SubTaskStatus;
  type?: ERequestType;
}

export type DateQuery = {
  startDate?: string;
  endDate?: string;
};

const defaultDate: DateQuery = {
  startDate: null,
  endDate: null,
};
export const types = {
  MAD: "D. Courier",
  NT: "N.Transport",
  TR: "Paps Tour",
  MSG: "Messagerie",
  COURSE: "Course",
};

export const missionTypes = {
  MAD: "Mise_a_disposition",
  MSG: "Messagerie",
  NT: "National_transport",
  TR: "Tour",
};

export const TaskList = ({ defaultTapvalue = 0, type }: TaskProps) => {
  const styles = useStyles();
  const { text } = useText();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [task, setTask] = useState(null);
  const [formDate, setFormDate] = React.useState(defaultDate);
  const [dataToSend, setDataToSend] = React.useState<DateQuery>(null);
  const [open, setOpen] = useState(false);
  const [isUpdate, update] = useState(false);
  const [status, setStatus] = useState("NOT_STARTED");
  const TaskColumn = taskColumns();
  const [confirm, setConfirm] = useState(false);
  const [toReprogram, setToreprogram] = useState<SubTask>(null);
  const [key, setKey] = useState<string>(MissionStatusType.NotStarted);
  const [page, setPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [taskRechedules] = useMutation(SUBTASK_RESCHEDULING_BY_CLIENT);
  const dispatchAlert = useAlertDispatch();
  const [formValues, setFormvalues] = useState(null);
  const taskActions = subtaskActions(text);
  const [, setIsSubmited] = useState(false);
  const [msgCount, setMsgCount] = React.useState(0);
  const [madCount, setMadCount] = useState(0);
  const [ntCount, setNtCount] = useState(0);
  const [trCount, setTrCount] = useState(0);
  const [crsCount, setCrsCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const tabList = useTabs(madCount, trCount, msgCount, ntCount, crsCount);
  const [search, setValue] = React.useState<string>("");
  const [cords, setCords] = React.useState(null);
  const [missions, setMissions] = useState<Mission[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [madTasks, setMadTasks] = useState<Task[]>([]);
  const { user } = useCurrentUser();
  const countryCode = useCountryCode();
  const [axe, setAxe] = useState(null);
  const [executionDate] = useState(formatDate(new Date(), "yyyy-MM-dd"));
  const [getAllMissions, { loading, data }] = useLazyQuery(GET_ALL_MISSIONS, { fetchPolicy: "network-only" });
  const [courseBage, { data: courseBageData }] = useLazyQuery(COURSE_BADGES, { fetchPolicy: "network-only" });
  const [course, { data: courseData, loading: courseLoading }] = useLazyQuery(GET_ALL_COURSES, {
    fetchPolicy: "network-only",
  });
  const [getMadTask, { data: madData, loading: madLoading }] = useLazyQuery(ALL_TASKS, {
    fetchPolicy: "network-only",
  });
  const [badge, setBadge] = useState([]);
  const [getMissionsBadge, { data: badgeData }] = useLazyQuery(FETCH_MISSION_BADGE_BY_TYPES, {
    fetchPolicy: "network-only",
  });

  const onSetRowPerPage = (props: number) => {
    setRowsPerPage(props);
  };

  React.useEffect(() => {
    getMissionsBadge({
      variables: {
        queries: {
          clientId: user?.company_user_clientId,
          badges: [
            {
              key: missionTypes.MAD,
              type: "MAD",
            },
            {
              key: missionTypes.MSG,
              type: "MSG",
            },
            {
              key: missionTypes.NT,
              type: "NT",
            },
            {
              key: missionTypes.TR,
              type: "TR",
            },
          ],
        },
      },
    });
  }, []);

  React.useEffect(() => {
    course({
      variables: {
        queries: {
          page: page + 1,
          limit: rowsPerPage,
          client: user?.company_user_clientId,
          clientType: "ENTERPRISE",
          countryCodes: [countryCode],
        },
      },
    });
    getMadTask({
      variables: {
        queries: {
          page: page + 1,
          limit: rowsPerPage,
          idClient: user?.company_user_clientId,
          dateFrom: dataToSend?.startDate,
          dateTo: dataToSend?.endDate,
          type: [TaskType.Mad],
        },
      },
    });
  }, [page, rowsPerPage, status]);

  React.useEffect(() => {
    if (courseData) {
      setCourses(courseData?.results?.data);
    }
  }, [courseData]);
  React.useEffect(() => {
    if (madData) {
      setMadTasks(madData?.results?.data);
      setMadCount(madData?.results?.countTotal);
    }
  }, [madData]);

  React.useEffect(() => {
    courseBage({
      variables: {
        queries: {
          clientId: user?.company_user_clientId,
          countryCodes: [countryCode],
        },
      },
    });
  }, [courseBageData]);

  const getBadgeValueTab = React.useCallback(
    (key: string) => badge?.find((badge: any) => badge?.key === key)?.value || 0,
    [badge]
  );

  React.useEffect(() => {
    if (isSuccess(badgeData)) {
      setBadge(badgeData?.results?.data);
      setMsgCount(getBadgeValueTab(missionTypes.MSG));

      setNtCount(getBadgeValueTab(missionTypes.NT));
      setTrCount(getBadgeValueTab(missionTypes.TR));
    }
    if (courseBageData) {
      setCrsCount(courseBageData?.badges?.count);
    }
  });

  React.useEffect(() => {
    const queries = {
      page: page + 1,
      limit: rowsPerPage,
      clientId: user?.company_user_clientId,
      startDate: dataToSend?.startDate,
      endDate: dataToSend?.endDate,
      countryCodes: ["SN"],
      types: ["MSG"],
    };

    switch (status) {
      case types.MSG:
        queries.types = ["MSG"];
        break;
      case types.MAD:
        queries.types = ["MAD"];
        break;
      case types.NT:
        queries.types = ["NT"];
        break;
      case types.TR:
        queries.types = ["TR"];
        break;
      case types.COURSE:
        queries.types = ["COURSE"];
        break;

      default:
        break;
    }

    getAllMissions({
      variables: {
        queries: {
          ...queries,
        },
      },
    });
  }, [page, rowsPerPage, status, dataToSend]);
  React.useEffect(() => {
    if (isSuccess(data)) {
      setMissions(data?.results?.data);
    }
  }, [data, status]);
  React.useEffect(() => {
    navigator.geolocation.getCurrentPosition((position) => {
      setCords({ lat: position.coords.latitude, long: position.coords.longitude });
    });
  }, [task]);

  const handleClose = () => {
    setOpenModal(false);
    setIsSubmited(false);
  };

  const handleConfirm = (reprogram: { date: string; time: string }, t: SubTask) => {
    setToreprogram(t);
    setFormvalues(reprogram);
    update(false);
    setConfirm(true);
    setIsSubmited(false);
  };

  const handler = () => {
    taskRechedules({
      variables: {
        input: {
          subtaskID: toReprogram?._id,
          execution_date: formValues?.date,
          execution_slot_start: formValues?.time,
          execution_slot_end: formValues?.time === "11:00" ? "13:00" : "17:00",
        },
      },
    })
      .then((response) => {
        const { code, message } = response.data.subtaskReschedulingByClient;
        dispatchAlert({ severity: code === 200 ? "success" : "error", message });
      })
      .catch((error) => {
        dispatchAlert({ severity: "error", message: "Error from the server ..." });
      });
  };

  const handleChangePage = (event: React.ChangeEvent<{}>, newpage: number) => {
    setPage(newpage);
  };

  const actionSwitcher = (tab: TabProps) => {
    setTask(null);
    setStatus(tab?.libelle);
    setKey(tab.id);
  };

  const dateFrom = (e: React.FocusEvent<HTMLInputElement>) => {
    setFormDate({
      ...formDate,
      startDate: e.target.value,
    });
  };
  const dateTo = (e: React.FocusEvent<HTMLInputElement>) => {
    setFormDate({
      ...formDate,
      endDate: e.target.value,
    });
  };
  const handleConfirmDate = () => {
    setIsSubmited(true);
    setOpenModal(false);
    setDataToSend(formDate);
  };
  const handleFilter = () => setOpenModal(true);

  const onSearch = (event: any) => {
    event.preventDefault();
    event.persist();
    setValue(event.target.value);
  };

  const TasksDetailsComponent = (mission?: Mission) => {
    switch (task.type) {
      case ERequestType.Tr:
        return (
          <>
            <TaskDetailTitle
              stops={[]}
              creationDate={mission?.executionDate}
              vehicle={null}
              service={PapsService.Tr}
              runsheet={null}
            />

            <TaskDetailsTR mission={mission} />
          </>
        );

      case ERequestType.Nt:
        return parseTourStops(task?.stops)?.map((stop, index) => (
          <>
            {!index && (
              <TaskDetailTitle
                stops={task?.axes?.flatMap((axe: any) => axe.stops)}
                creationDate={task?.createdAt}
                vehicle={task?.vehicles[0]}
                service={PapsService.Nt}
                runsheet={task?.runsheet}
                driver={task?.task_driver}
              />
            )}
            <TaskDetailsNT key={index} isLast={index === task?.stops.length - 1} stop={stop} />
          </>
        ));
      case ERequestType.Msg:
        return (
          <>
            <TaskDetailTitle
              stops={task?.axes?.flatMap((axe: any) => axe.stops)}
              creationDate={task?.createdAt}
              service={PapsService.Msg}
              runsheet={task?.runsheet}
              driver={task?.task_driver}
            />
            {task?.axes.flatMap((axe: Axe, index: number) => (
              <TaskDetailsMsg key={index} isLastAxe={index === task?.axes.length - 1} axe={axe} />
            ))}
          </>
        );
      default:
        return null;
    }
  };

  React.useEffect(() => {
    if (task && task?.type === ERequestType.Msg) {
      setAxe(task?.axes);
    }
  }, [task]);

  const lists: any = React.useMemo(() => (search ? makeSearch(missions, search) : missions), [search, missions]);

  const isCurrentRow = (data: Mission) => data?._id === task?._id;

  const renderMADTask = () => {
    return (
      <>
        <TaskDetailTitle
          stops={[]}
          creationDate={task?.createdAt}
          vehicle={task?.task_vehicle_type}
          service={PapsService.Mad}
          runsheet={task?.runsheet}
          driver={task?.task_driver}
          task={task}
        />
        <TaskDetailsMAD task={task} isLast />
      </>
    );
  };

  const [tableData, countTotal] = React.useMemo(() => {
    if (key === "MAD") {
      return [madTasks, madCount];
    }
    if (status === types.COURSE) {
      return [courses, crsCount];
    }
    if (status === types.TR) {
      return [lists, trCount];
    }
    if (status === types.NT) {
      return [lists, ntCount];
    }
    return [lists, msgCount];
  }, [lists, madTasks, courses, status]);

  return (
    <div className={styles.root}>
      <Grid
        className={styles.left}
        style={{
          width: isSmallScreen ? "100%" : "75%",
        }}
      >
        <div className={styles.top}>
          <Slogan isTask />
        </div>
        <div className={styles.bottom}>
          <div className={styles.contain}>
            <Typography className={styles.titleTask}>{text.all.my_tasks}</Typography>
            <div className={styles.searchFilter}>
              <SearchBox value={search} onChange={onSearch} placeholder={`Search ${text.all.areas}`} />

              <ThemeProvider theme={styleIcon}>
                <Button
                  className={styles.button}
                  aria-label="more"
                  aria-controls="customized-menu"
                  aria-haspopup="true"
                  variant="outlined"
                  endIcon={<FilterList />}
                  onClick={handleFilter}
                >
                  <span className={styles.filterStyle}> {text.parcel.filtrer_text} </span>
                </Button>
              </ThemeProvider>

              <Dialog
                title={text.all.filter_by_date}
                open={openModal}
                maxWidth="xs"
                handleConfirm={handleConfirmDate}
                handleClose={handleClose}
                confirmButtonTitle={text.all.validate}
                cancelButtonTitle={text.all.cancel}
              >
                <Grid container spacing={1} className={styles.searchDateTask}>
                  <Grid item xs={12} className={styles.searchDate}>
                    <TextField
                      type="date"
                      fullWidth
                      variant="outlined"
                      label={text.all.from}
                      name="date"
                      value={formDate.startDate}
                      onChange={dateFrom}
                      className={styles.textField}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{ max: formDate.endDate }}
                    />
                  </Grid>
                  <Grid item xs={12} className={styles.searchDate}>
                    <TextField
                      type="date"
                      fullWidth
                      variant="outlined"
                      label={text.all.to}
                      name="date"
                      value={formDate.endDate}
                      onChange={dateTo}
                      className={styles.textField}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{ min: formDate.startDate }}
                    />
                  </Grid>
                </Grid>
              </Dialog>
            </div>
          </div>
          <Grid item xs={isSmallScreen ? 12 : null}>
            <NavSwitcher
              withBadge
              onChange={(tab) => {
                actionSwitcher(tab);
              }}
              tabs={tabList}
              defaultValue={defaultTapvalue}
              isTask
            />
          </Grid>
          <div className={styles.table}>
            <Table
              data={tableData}
              rowsIdentifier="_id"
              columns={TaskColumn}
              isLoading={loading || madLoading || courseLoading}
              isCurrentRow={isCurrentRow}
              onRowClick={(row) => {
                setTask(row);
              }}
              type={[types.COURSE, "MAD"].includes(key) ? key : null}
              isShowMenu
              statusKey={key}
              multipleActions={taskActions}
              advancedControl
              rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
              rowsPerPage={rowsPerPage}
              currentPage={page}
              countTotal={countTotal}
              onChangePage={handleChangePage}
              setRowsPerPage={onSetRowPerPage}
              searchTerm={search}
            />
          </div>
        </div>
      </Grid>
      <Slider openDrawer={Boolean(task)} handleClose={() => setTask(null)}>
        <div className={styles.MapContainer}>
          <div className={styles.gridTask}>
            {task ? (
              <div className={styles.taskDetail}>
                {key === "MAD" ? renderMADTask() : TasksDetailsComponent(task) || <CourseDetails course={task} />}
              </div>
            ) : (
              <DefaultInfo />
            )}
          </div>
          <div></div>
          {/* <div>
            <GoogleMap
              id="map"
              height={task?.type === ERequestType.Nt ? "85.6vh" : "80vh"}
              options={{
                center: {
                  lat: cords?.lat,
                  lng: cords?.long,
                },
                zoom: 8,
                disableDefaultUI: true,
              }}
              taskType={task?.type}
              stops={task?.type === ERequestType.Nt ? task?.stops : getMsgStops(task)}
              destination={task?.stops[0]?.address?.place_id}
              orgin={task?.stops[1]?.address?.place_id}
              travelMode="DRIVING"
            />
          </div> */}
        </div>
      </Slider>
    </div>
  );
};

export default TaskList;
